import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getRegnskaber } from '../lib/cms';

export default function Regnskaber() {
  const [regnskaber, setRegnskaber] = useState([]);

  useEffect(() => {
    getRegnskaber().then(setRegnskaber);
  }, []);

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Regnskaber - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Officielle årsregnskaber og donationsoversigt for Foreningen ØrBike (CVR: 45950433)."
        />
        <meta property="og:title" content="Regnskaber - Foreningen ØrBike" />
        <meta property="og:description" content="Se økonomiske nøgletal og officielle regnskaber for Foreningen ØrBike." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#131526] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            Økonomisk gennemsigtighed
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Regnskaber
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Foreningen ØrBike (CVR: 45950433)
          </p>
        </div>
      </section>

      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Foreningen ØrBike er en frivillig forening stiftet med henblik på at arrangere det årlige velgørenhedsevent i Ørbæk Midtpunkt. Herunder findes foreningens årsregnskaber og økonomiske opgørelser.
          </p>
        </div>

        <div className="space-y-4">
          {regnskaber.map((regnskab, i) => (
            <div
              key={regnskab.id || i}
              className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg"
            >
              <div className="flex items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pink-500/20 border border-pink-500/30 text-pink-400 flex items-center justify-center text-xl shrink-0">
                  <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-display uppercase">
                    {regnskab.titel}
                  </h3>
                  {regnskab.beskrivelse && (
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      {regnskab.beskrivelse}
                    </p>
                  )}
                  {regnskab.publiceringsdato && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Offentliggjort: {regnskab.publiceringsdato}
                    </div>
                  )}
                </div>
              </div>

              <div className="sm:shrink-0 w-full sm:w-auto">
                {regnskab.pdf_fil?.[0] ? (
                  <a
                    href={regnskab.pdf_fil[0]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow"
                  >
                    <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                    <span>Åbn PDF</span>
                  </a>
                ) : (
                  <div className="text-xs text-slate-500 italic bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-800">
                    PDF under opdatering
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Foreningens Vedtægter */}
        <div className="p-6 sm:p-8 rounded-2xl bg-[#12141f] border border-purple-500/30 hover:border-purple-500/50 transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-lg">
          <div className="flex items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-400 flex items-center justify-center text-xl shrink-0">
              <i className="fa-solid fa-scroll" aria-hidden="true"></i>
            </div>
            <div>
              <span className="text-[11px] font-mono uppercase font-bold text-purple-400 tracking-wider">Officielt dokument</span>
              <h3 className="text-lg sm:text-xl font-bold text-white font-display uppercase">
                Vedtægter for Foreningen ØrBike
              </h3>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed max-w-xl">
                Officielle vedtægter for Foreningen ØrBike (Hjemsted: Svendborg Kommune, CVR: 45950433) – Frivillighed, fællesskab og velgørenhed.
              </p>
              <div className="text-[11px] text-slate-500 mt-1">
                Version 09-2026 • Revision foretages af Team Rynkeby Fondens økonomiafdeling
              </div>
            </div>
          </div>

          <div className="sm:shrink-0 w-full sm:w-auto">
            <a
              href="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/foreningen-oerbike-vedtaegter-09-2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow"
            >
              <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
              <span>Åbn Vedtægter (PDF)</span>
            </a>
          </div>
        </div>

        {/* CVR Fakta Boks */}
        <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400 leading-relaxed text-center space-y-1">
          <p>
            Revision foretages af Team Rynkeby Fondens økonomiafdeling.
          </p>
          <p>
            Spørgsmål til regnskaber eller bogholderi kan rettes direkte til <a href="mailto:info@orbike.dk" className="text-pink-400 underline">info@orbike.dk</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
